import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';

interface EditSessionWarningProps {
  open: boolean;
  onClose: () => void;
  onTakeOver: () => void;
  onContinue: () => void;
  currentEditor?: {
    username: string;
    userRole: string;
    startedAt: string;
    lastActivity: string;
  };
  resourceName: string;
  canTakeOver: boolean;
  loading?: boolean;
}

const EditSessionWarning: React.FC<EditSessionWarningProps> = ({
  open,
  onClose,
  onTakeOver,
  onContinue,
  currentEditor,
  resourceName,
  canTakeOver,
  loading = false
}) => {
  const [timeSinceStart, setTimeSinceStart] = useState<string>('');

  useEffect(() => {
    if (!currentEditor?.startedAt) return;
    
    const updateTime = () => {
      const startTime = new Date(currentEditor.startedAt);
      const now = new Date();
      const diffMs = now.getTime() - startTime.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffSecs = Math.floor((diffMs % 60000) / 1000);
      
      if (diffMins > 0) {
        setTimeSinceStart(`${diffMins}m ${diffSecs}s`);
      } else {
        setTimeSinceStart(`${diffSecs}s`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [currentEditor?.startedAt]);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('sr-RS', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Resurs je već u upotrebi
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{resourceName}</strong> trenutno edituje drugi korisnik.
          </Typography>
          {canTakeOver && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Kao administrator, možete da preuzmete kontrolu nad ovim resursom.
            </Typography>
          )}
        </Alert>

        {currentEditor && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Trenutni editor:
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon color="primary" />
              <Typography variant="body1">
                <strong>{currentEditor.username}</strong>
              </Typography>
              <Chip 
                label={currentEditor.userRole} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimeIcon color="action" />
              <Typography variant="body2" color="textSecondary">
                Započeto: {formatTime(currentEditor.startedAt)} ({timeSinceStart} ago)
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ComputerIcon color="action" />
              <Typography variant="body2" color="textSecondary">
                Poslednja aktivnost: {formatTime(currentEditor.lastActivity)}
              </Typography>
            </Box>
          </Box>
        )}

        {canTakeOver && (() => {
          const sessionAge = currentEditor?.lastActivity ? 
            Date.now() - new Date(currentEditor.lastActivity).getTime() : 0;
          const isSessionActive = sessionAge <= 5 * 60 * 1000; // 5 minuta
          
          return isSessionActive ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Kao admin, možete preuzeti ovu sesiju i nastaviti sa editovanjem.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Sesija je istekla. Možete pokušati da započnete novu sesiju.
              </Typography>
            </Alert>
          );
        })()}
        
        {!canTakeOver && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Možete da proverite ponovo za 5 sekundi da li je resurs oslobođen.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Vrati na dashboard
        </Button>
        
        {canTakeOver && (() => {
          const sessionAge = currentEditor?.lastActivity ? 
            Date.now() - new Date(currentEditor.lastActivity).getTime() : 0;
          const isSessionActive = sessionAge <= 5 * 60 * 1000; // 5 minuta
          
          return isSessionActive ? (
            <Button 
              onClick={onTakeOver} 
              variant="contained" 
              color="warning"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              Preuzmi kontrolu i nastavi editovanje
            </Button>
          ) : (
            <Button 
              onClick={onContinue} 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              Pokušaj ponovo
            </Button>
          );
        })()}
        
        {!canTakeOver && (
          <Button 
            onClick={onContinue} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            Proveri ponovo za 5s
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditSessionWarning; 